import { useCallback, useEffect, useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  DataCell,
  DataRow,
  DataTable,
} from "@breakingthelines/design-system";

import { MIN_REASON_LENGTH } from "@/lib/advertisers";
import {
  listPlatformPageRevisions,
  restorePlatformPageRevision,
  type PlatformPageRevision,
  type PlatformPageSummary,
} from "@/lib/admin-api";

import styles from "./page-history.module.css";

/**
 * Earlier versions of one platform page, and the restore.
 *
 * # WHAT A ROW IS
 *
 * content-service snapshots on write, so a row is the text as it stood BEFORE
 * a change replaced it. The text the page carries right now is NOT a row. The
 * lede says so, because without it the top row reads as "what the page says
 * today" and restoring it looks broken.
 *
 * # WHY THE RESTORE LIVES HERE AND NOT ON ITS OWN SCREEN
 *
 * The host is the editor page, so restoring has to hand the restored document
 * back to a mounted editor. `onRestored` is that seam; page-editor.tsx owns
 * what happens to the editor, because that is where the never-blank rule and
 * the seeding contract live. This component knows nothing about Lexical.
 */

export function formatRevisionDate(value: string | null | undefined): string {
  if (!value) return "Unknown";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Unknown";
  return date.toLocaleString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * Who saved a version, in the order an operator can act on: name, then handle,
 * then the id. An id is worse than a name and better than a blank cell, which
 * is what a version saved before edits were attributed would otherwise show.
 */
export function revisionAuthor(revision: PlatformPageRevision): string {
  return revision.savedByName || revision.savedByUsername || revision.savedById || "Unknown";
}

/** What replaced a version, in operator words. */
export function replacedByLabel(replacedBy: string): string {
  switch (replacedBy) {
    case "edit":
      return "An edit";
    case "reset":
      return "A reset";
    case "restore":
      return "A restore";
    default:
      return "A change";
  }
}

export function PageHistory({
  page,
  onRestored,
}: {
  page: PlatformPageSummary;
  /**
   * Called with the page as it now stands, restored. The editor reseeds from
   * it; see page-editor.tsx.
   */
  onRestored: (restored: PlatformPageSummary) => void;
}) {
  const [revisions, setRevisions] = useState<PlatformPageRevision[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [target, setTarget] = useState<PlatformPageRevision | null>(null);
  const [reason, setReason] = useState("");
  const [reasonTouched, setReasonTouched] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [restoreError, setRestoreError] = useState<string | null>(null);

  const load = useCallback(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);

    listPlatformPageRevisions(page.slug)
      .then((data) => {
        if (cancelled) return;
        setRevisions(data.revisions ?? []);
      })
      .catch((err: Error) => {
        if (cancelled) return;
        setLoadError(err.message ?? "Failed to load earlier versions");
        setRevisions([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [page.slug]);

  useEffect(() => load(), [load]);

  const reasonValid = reason.trim().length >= MIN_REASON_LENGTH;

  const handleRestore = useCallback(async () => {
    if (!target || !reasonValid || restoring) return;
    setRestoring(true);
    setRestoreError(null);
    try {
      const restored = await restorePlatformPageRevision({
        pageSlug: page.slug,
        revisionId: target.revisionId,
        reason: reason.trim(),
        expectedVersion: page.version,
      });
      setTarget(null);
      setReason("");
      setReasonTouched(false);
      onRestored(restored);
      // The restore made a new version out of the text it replaced, so the
      // list is one row short the moment it succeeds.
      load();
    } catch (err) {
      setRestoreError((err as Error).message ?? "Failed to restore");
    } finally {
      setRestoring(false);
    }
  }, [target, reasonValid, restoring, page.slug, page.version, reason, onRestored, load]);

  return (
    <section className={styles.history}>
      <h2 className={styles.title}>Earlier versions</h2>
      <p className={styles.lede}>
        What this page said before each change. The text it carries now is not listed.
      </p>

      {loading ? <p className={styles.notice}>Loading earlier versions…</p> : null}
      {loadError ? <p className={styles.error}>{loadError}</p> : null}

      {!loading && !loadError && revisions.length === 0 ? (
        <p className={styles.notice}>Nothing yet. The first change to this page starts the list.</p>
      ) : null}

      {!loading && !loadError && revisions.length > 0 ? (
        <DataTable
          label="Earlier versions"
          columns="minmax(160px, 1.2fr) minmax(150px, 1fr) minmax(140px, 0.9fr) minmax(160px, 1.2fr) minmax(110px, 0.7fr)"
          header={["Version", "Saved", "Saved by", "Replaced", ""]}
        >
          {revisions.map((revision) => (
            <DataRow key={revision.revisionId} className="text-sm">
              <DataCell>{revision.title || "Untitled"}</DataCell>
              <DataCell label="Saved">{formatRevisionDate(revision.savedAt)}</DataCell>
              <DataCell label="Saved by" className="text-muted-foreground">
                {revisionAuthor(revision)}
              </DataCell>
              <DataCell label="Replaced" className="text-muted-foreground">
                <div>
                  {replacedByLabel(revision.replacedBy)}, {formatRevisionDate(revision.replacedAt)}
                </div>
                {revision.reason ? <div className={styles.reason}>{revision.reason}</div> : null}
              </DataCell>
              <DataCell>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    setTarget(revision);
                    setReason("");
                    setReasonTouched(false);
                    setRestoreError(null);
                  }}
                >
                  Restore
                </Button>
              </DataCell>
            </DataRow>
          ))}
        </DataTable>
      ) : null}

      {restoreError ? <p className={styles.error}>{restoreError}</p> : null}

      {/*
       * An AlertDialog, like the promo deactivate and the membership revoke:
       * Base UI hardcodes disablePointerDismissal on confirmations, so an
       * outside click cannot rewrite a legal page. The reason field sits
       * inside it because the confirm and the reason gate the same call, so
       * there is no path from the row button to the API without both.
       */}
      <AlertDialog
        open={target != null}
        onOpenChange={(open) => {
          if (open) return;
          setTarget(null);
          setReason("");
          setReasonTouched(false);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Restore this version</AlertDialogTitle>
            <AlertDialogDescription>
              The page goes back to the text saved on {formatRevisionDate(target?.savedAt)}. The
              text it carries now is kept and can be restored. Unsaved edits are lost.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>Reason</span>
            <textarea
              className={styles.textarea}
              rows={2}
              value={reason}
              onChange={(event) => setReason(event.currentTarget.value)}
              onBlur={() => setReasonTouched(true)}
            />
            {reasonTouched && !reasonValid ? (
              <span className={styles.errorText}>
                Reason must be at least {MIN_REASON_LENGTH} characters.
              </span>
            ) : null}
          </label>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={restoring}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={!reasonValid || restoring}
              onClick={() => void handleRestore()}
            >
              {restoring ? "Restoring…" : "Restore version"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
